import { useCallback, useRef, useState } from "react";
import { isAcceptedImage } from "../utils/imageRegistry";

interface Props {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

export function Uploader({ onFiles, disabled }: Props) {
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const handleFiles = useCallback(
    (fileList: FileList | null) => {
      if (!fileList || fileList.length === 0) return;
      const valid: File[] = [];
      const rejected: string[] = [];
      for (const f of Array.from(fileList)) {
        if (isAcceptedImage(f)) valid.push(f);
        else rejected.push(f.name);
      }
      if (rejected.length > 0) {
        setError(`Skipped non-image files: ${rejected.join(", ")}`);
      } else {
        setError(null);
      }
      if (valid.length > 0) onFiles(valid);
    },
    [onFiles],
  );

  function onDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (!disabled) setDragOver(true);
  }
  function onDragLeave(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
  }
  function onDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    setDragOver(false);
    if (disabled) return;
    handleFiles(e.dataTransfer.files);
  }

  return (
    <div className="uploader">
      <div
        className={`dropzone ${dragOver ? "over" : ""} ${disabled ? "disabled" : ""}`}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={() => !disabled && inputRef.current?.click()}
      >
        <div className="dz-title">Drag & drop images here</div>
        <div className="dz-sub">or click to choose files (PNG, JPG, WEBP)</div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          handleFiles(e.target.files);
          // Allow re-selecting the same file.
          e.target.value = "";
        }}
      />
      {error && <div className="uploader-error">{error}</div>}
    </div>
  );
}
